/**
 * "Is a newer build live than the one running in this tab?"
 *
 * A tab opened before a deployment keeps the old bundle — the service worker
 * swaps itself only when the browser checks it again — and then produces
 * errors that look real. The build writes `version.json` from the same stamp
 * as the footer (vite.config.ts); the page asks for it every few minutes and
 * when it becomes visible again, and offers a reload. It never reloads by
 * itself: a half-filled report would be lost.
 *
 * Served with `Cache-Control: no-store` and outside the navigation fallback
 * (public/staticwebapp.config.json): without the exclusion a missing file
 * answers 200 with index.html, which is why this parses defensively.
 */
export interface Stamp {
  readonly build: string;
  readonly commit: string;
  readonly builtAt: string;
}

export const VERSION_FILE = '/version.json';
export const VERSION_POLL_MS = 5 * 60 * 1000;

/** The file's content; the same three fields as the footer and /api/health. */
export function versionJson(stamp: Stamp): string {
  return `${JSON.stringify({ build: stamp.build, commit: stamp.commit, builtAt: stamp.builtAt })}\n`;
}

/** Does this bundle ask at all? A local `dev` build has nothing to compare. */
export function checksForUpdates(running: Stamp): boolean {
  return running.commit !== 'dev';
}

/** True only for a well-formed stamp of another real build. */
export function isUpdate(running: Stamp, served: unknown): boolean {
  if (!checksForUpdates(running)) return false;
  if (typeof served !== 'object' || served === null) return false;
  const commit = (served as { commit?: unknown }).commit;
  return typeof commit === 'string' && commit !== '' && commit !== 'dev' && commit !== running.commit;
}

/** One question to the server; any failure (offline, HTML, 404) means "no news". */
export async function fetchServedStamp(fetcher: typeof fetch = fetch): Promise<unknown> {
  try {
    const res = await fetcher(VERSION_FILE, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

/** How long the reload waits for the new service worker before it gives up on it. */
export const RELOAD_SW_WAIT_MS = 10_000;

/** The parts of the browser the reload touches; a parameter so the order can be tested. */
export interface ReloadEnv {
  readonly serviceWorker?: Pick<ServiceWorkerContainer, 'getRegistrations' | 'addEventListener' | 'removeEventListener'> | undefined;
  readonly caches?: Pick<CacheStorage, 'keys' | 'delete'> | undefined;
  readonly reload: () => void;
  readonly waitMs?: number;
}

/**
 * "Neu laden" that really loads the new build. A plain location.reload() is
 * answered by the OLD service worker from its precache — the old index.html,
 * the old bundle — and only swaps afterwards, so the band came back and the
 * second reload worked (owner, 29.10.2026). So: ask the worker to look for the
 * new build; it installs it and takes over at once (vite-plugin-pwa
 * autoUpdate: skipWaiting + clientsClaim), which `controllerchange` reports;
 * then reload. If it does not take over in time, unregister it and drop its
 * caches: the reload then comes from the network and registers it afresh.
 * Only reached after /version.json answered, i.e. online.
 */
export async function reloadToLatest(env: ReloadEnv): Promise<void> {
  const sw = env.serviceWorker;
  try {
    const regs = sw ? await sw.getRegistrations() : [];
    if (sw && regs.length) {
      const swapped = new Promise<boolean>((resolve) => {
        const done = () => resolve(true);
        sw.addEventListener('controllerchange', done, { once: true });
        setTimeout(() => {
          sw.removeEventListener('controllerchange', done);
          resolve(false);
        }, env.waitMs ?? RELOAD_SW_WAIT_MS);
      });
      await Promise.all(regs.map((r) => r.update().catch(() => undefined)));
      if (!(await swapped)) {
        await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
        if (env.caches) await Promise.all((await env.caches.keys()).map((k) => env.caches!.delete(k)));
      }
    }
  } catch {
    // Whatever failed, a reload is still the best this button can do.
  }
  env.reload();
}

/** reloadToLatest with the real browser. */
export function reloadPage(): Promise<void> {
  return reloadToLatest({
    serviceWorker: 'serviceWorker' in navigator ? navigator.serviceWorker : undefined,
    caches: typeof caches === 'undefined' ? undefined : caches,
    reload: () => window.location.reload(),
  });
}
